import { motion } from "framer-motion";
import { Link } from "react-router-dom";

/**
 * Reusable hero-right-slot visual with orbit rings, centered card, and floating cards.
 * Each page passes its own center content + floating items config.
 *
 * Props:
 *   center: { logo?: string, icon?: ReactNode, topLabel?: string, bottomLabel?: string }
 *   cards: [{ position, rotate, icon, color, label, value, sub, delay }]
 *   accentColor?: background glow color
 *   height?: "default" | "tall"
 */
export default function HeroVisual({ center, cards = [], accentColor = "#FF3B30", height = "default" }) {
  const hCls = height === "tall" ? "h-[460px] md:h-[540px]" : "h-[420px] md:h-[500px]";
  return (
    <div className={`relative ${hCls} hidden md:block`} data-testid="page-hero-visual">
      {/* Glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[420px] h-[420px] rounded-full blur-[100px]" style={{ background: `${accentColor}33` }}></div>
      </div>

      {/* Orbits */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[360px] h-[360px] rounded-full border border-dashed border-white/10"></div>
        <div className="absolute w-[260px] h-[260px] rounded-full border border-dashed border-white/10"></div>
      </div>

      {/* Center card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] border flex flex-col items-center justify-center text-center px-3"
        style={{ borderColor: `${accentColor}4D`, boxShadow: `0 20px 80px -10px ${accentColor}66` }}
      >
        {center.logo && <img src={center.logo} alt="" className="h-14 w-14 mb-3" />}
        {center.icon && <div className="mb-3" style={{ color: accentColor }}>{center.icon}</div>}
        {center.topLabel && <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: accentColor }}>{center.topLabel}</div>}
        {center.bottomLabel && <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/60 mt-0.5">{center.bottomLabel}</div>}
      </motion.div>

      {/* Floating cards */}
      {cards.map((c, i) => (
        <FloatingCard key={i} {...c} />
      ))}
    </div>
  );
}

const POS_CLS = {
  tl: "top-2 left-0 lg:left-4",
  tr: "top-2 right-0 lg:right-4",
  ml: "top-[42%] left-0",
  mr: "top-[42%] right-0",
  bl: "bottom-2 left-2 lg:left-8",
  br: "bottom-2 right-2 lg:right-8",
};

function FloatingCard({ position = "tr", rotate = 0, icon, color = "#FF3B30", label, value, sub, delay = 0.4, href }) {
  const initialX = position.includes("l") ? -12 : position.includes("r") ? 12 : 0;
  const initialY = position.startsWith("t") ? -10 : position.startsWith("b") ? 14 : 0;
  const base = (
    <motion.div
      initial={{ opacity: 0, x: initialX, y: initialY, rotate }} animate={{ opacity: 1, x: 0, y: 0, rotate }}
      transition={{ duration: 0.6, delay }}
      className={`absolute ${POS_CLS[position]} rounded-2xl bg-white/[0.05] backdrop-blur border border-white/10 px-5 py-4 w-[180px]`}
    >
      <div className="flex items-center gap-2 mb-2" style={{ color }}>
        {icon}
        {label && <span className="text-[10px] tracking-[0.2em] uppercase font-bold">{label}</span>}
      </div>
      {value && <div className="font-display text-2xl lg:text-3xl font-extrabold text-white leading-none">{value}</div>}
      {sub && <div className="text-[11px] text-white/50 mt-1 leading-tight">{sub}</div>}
    </motion.div>
  );
  return href ? <Link to={href} className="block">{base}</Link> : base;
}
